/**
 * SlidePanel - Reusable slide-out panel component
 * Uses Fluent UI Drawer for consistent slide panel behavior
 */

import React from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerHeaderNavigation,
  Button,
  makeStyles,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';

export type SlidePanelSize = 'small' | 'medium' | 'large' | 'full';
export type SlidePanelPosition = 'start' | 'end';

interface SlidePanelProps {
  /** Whether the panel is open */
  open: boolean;
  /** Callback when panel open state changes */
  onOpenChange: (open: boolean) => void;
  /** Panel title */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Panel content */
  children: React.ReactNode;
  /** Panel size - small (320px), medium (480px), large (640px), full (100%) */
  size?: SlidePanelSize;
  /** Custom width override (e.g., '33%', '500px') - takes precedence over size */
  width?: string;
  /** Panel position - start (left) or end (right) */
  position?: SlidePanelPosition;
  /** Footer content (typically action buttons) */
  footer?: React.ReactNode;
  /** Whether the panel is in a loading state */
  loading?: boolean;
  /** Loading message */
  loadingMessage?: string;
  /** Icon to display next to the title */
  icon?: React.ReactNode;
}

const useStyles = makeStyles({
  header: {
    padding: '16px 24px',
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    paddingLeft: '0',
  },
  subtitle: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    fontWeight: 'normal',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '20px',
    fontWeight: '600',
  },
  titleIcon: {
    fontSize: '20px',
    color: tokens.colorBrandForeground1,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '0 24px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    padding: '16px 24px',
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
    gap: '16px',
  },
});

export const SlidePanel: React.FC<SlidePanelProps> = ({
  open,
  onOpenChange,
  title,
  subtitle,
  children,
  size = 'medium',
  width,
  position = 'end',
  footer,
  loading = false,
  loadingMessage = 'Loading...',
  icon,
}) => {
  const styles = useStyles();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOpenChange = (_: any, data: any) => {
    // Only allow closing via explicit button clicks, not backdrop/escape
    if (data.type === 'backdropClick' || data.type === 'escapeKeyDown') {
      return;
    }
    onOpenChange(data.open);
  };

  // Custom width style if provided
  const drawerStyle = width ? { width, maxWidth: width } : undefined;

  return (
    <Drawer
      type="overlay"
      position={position}
      open={open}
      onOpenChange={handleOpenChange}
      size={width ? undefined : size}
      style={drawerStyle}
    >
      <DrawerHeader className={styles.header}>
        <DrawerHeaderNavigation>
          <Button
            appearance="subtle"
            aria-label="Close panel"
            icon={<DismissRegular />}
            onClick={() => onOpenChange(false)}
          />
        </DrawerHeaderNavigation>
        <DrawerHeaderTitle>
          <div className={styles.headerContent}>
            <div className={styles.titleRow}>
              {icon && <span className={styles.titleIcon}>{icon}</span>}
              {title}
            </div>
            {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
          </div>
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={styles.body}>
        <div className={styles.content}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <Spinner size="medium" />
              <span>{loadingMessage}</span>
            </div>
          ) : (
            children
          )}
        </div>
        {footer && !loading && <div className={styles.footer}>{footer}</div>}
      </DrawerBody>
    </Drawer>
  );
};

export default SlidePanel;
